import React, { useState, useEffect, useRef } from "react";
import {
  FaPlay,
  FaPause,
  FaStop,
  FaSave,
  FaTrash,
  FaPlus,
  FaFileExport,
} from "react-icons/fa";
import { ClipLoader } from "react-spinners";
import { usePatternInsSeq } from "./PatternInsSeqContext";
import { useUser } from "./UserContext";
import { Databases, ID, Query } from "appwrite";
import client from "./appwriteConfig";
import InstrumentAudioContext from "./InstrumentAudioContext";
import VocalsAudioContext from "./VocalsAudioContext";
import "./css/SongPlayer.css"; // Import the CSS file

import ExportSong from "./ExportSong";
const databases = new Databases(client);

const generateNotes = () => {
  const notes = [];
  const noteNames = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
  ];
  for (let octave = 0; octave < 6; octave++) {
    for (const note of noteNames) {
      notes.push(`${note}${octave}`);
    }
  }
  return notes;
};

const notes = generateNotes();

const SongPlayer = ({ bpm, setBpm, pads, onEditPatternFromSong }) => {
  const { user } = useUser();
  const {
    fetchBeatPatterns,
    beatPatterns = [],
    fetchNotesPianoRoll,
  } = usePatternInsSeq();
  const [selectedPatterns, setSelectedPatterns] = useState(Array(5).fill(""));
  const [selectedVocalPatterns, setSelectedVocalPatterns] = useState(
    Array(5).fill(""),
  );
  const [vocalPatterns, setVocalPatterns] = useState([]);
  const [vocalPatternCache, setVocalPatternCache] = useState({});
  const [currentPatternIndex, setCurrentPatternIndex] = useState(0);
  const [instruments, setInstruments] = useState({});
  const [kits, setKits] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [ledIndex, setLedIndex] = useState(0); // Adjusted to start from 1
  const [patternCache, setPatternCache] = useState({});
  const [isSavingSong, setIsSavingSong] = useState(false);
  const [songName, setSongName] = useState("");
  const [songNames, setSongNames] = useState([]);
  const [songVolume, setSongVolume] = useState(0.5); // Volume range is 0 to 1
  const intervalRef = useRef(null);
  const patternCacheRef = useRef({});
  const selectedPatternsRef = useRef(selectedPatterns);
  const selectedVocalPatternsRef = useRef(selectedVocalPatterns);
  const currentPatternIndexRef = useRef(0);
  const ledIndexRef = useRef(0);
  const isPlayingRef = useRef(false);
  const playbackBusyRef = useRef(false);
  const [isDeletingSong, setIsDeletingSong] = useState(false);
  const [dropdownWidth, setDropdownWidth] = useState(62); // Initial width for the pattern dropdown
  const TIMELINE_GAP = 4;
  const [exportRequest, setExportRequest] = useState(0);
  const [isExportingSong, setIsExportingSong] = useState(false);
  const [timelineStep, setTimelineStep] = useState(0);

  const timelineStepRef = useRef(0);
  const activeBeatIndexRef = useRef(0);
  const activeVocalIndexRef = useRef(0);
  const [activeBeatIndex, setActiveBeatIndex] = useState(0);

  const [activeVocalIndex, setActiveVocalIndex] = useState(0);
  useEffect(() => {
    timelineStepRef.current = timelineStep;
  }, [timelineStep]);
  useEffect(() => {
    patternCacheRef.current = patternCache;
  }, [patternCache]);

  useEffect(() => {
    selectedPatternsRef.current = selectedPatterns;
  }, [selectedPatterns]);

  useEffect(() => {
    selectedVocalPatternsRef.current = selectedVocalPatterns;
  }, [selectedVocalPatterns]);

  useEffect(() => {
    currentPatternIndexRef.current = currentPatternIndex;
  }, [currentPatternIndex]);

  useEffect(() => {
    ledIndexRef.current = ledIndex;
  }, [ledIndex]);

  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);
  useEffect(() => {
    if (user) {
      fetchBeatPatterns();
      fetchSongNames();
      fetchVocalPatterns();
    }
  }, [user]);

  const fetchSongNames = async () => {
    try {
      const response = await databases.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_SONG_PATTERN_ID,
        [Query.equal("email", user.email)],
      );
      setSongNames(response.documents.map((doc) => doc.songName));
    } catch (error) {
      console.error("Failed to fetch song names:", error);
    }
  };
  const getVocalId = (vocal) => {
    return String(
      vocal?.$id || vocal?.documentId || vocal?.patternNumber || "",
    );
  };

  const getVocalName = (vocal) => {
    return (
      vocal?.vocalName || vocal?.patternName || vocal?.name || "Untitled Vocal"
    );
  };
  const fetchVocalPatterns = async () => {
    if (!user?.email) return;

    try {
      const response = await databases.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_VOCALS_ID,
        [
          Query.equal("userEmail", user.email),
          Query.orderDesc("$createdAt"),
          Query.limit(100),
        ],
      );

      setVocalPatterns(response.documents);

      const nextCache = {};

      response.documents.forEach((vocal) => {
        const vocalId = String(
          vocal.$id || vocal.documentId || vocal.patternNumber || "",
        );

        if (vocalId) {
          nextCache[vocalId] = vocal;
        }
      });

      setVocalPatternCache(nextCache);

      await Promise.all(
        response.documents
          .filter((vocal) => vocal.audioUrl)
          .map((vocal) => {
            const vocalId = getVocalId(vocal);

            if (!vocalId) return Promise.resolve();

            return VocalsAudioContext.preload(vocalId, vocal.audioUrl).catch(
              (error) => {
                console.error(
                  `Could not preload vocal ${getVocalName(vocal)}:`,
                  error,
                );
              },
            );
          }),
      );
    } catch (error) {
      console.error("Failed to fetch vocal patterns:", error);
    }
  };
  useEffect(() => {
    if (!user?.email) return;

    preloadSelectedSongPatterns();
  }, [selectedPatterns, selectedVocalPatterns, user?.email]);
  const handleSongVolumeChange = (event) => {
    const newVolume = parseFloat(event.target.value);

    setSongVolume(newVolume);

    InstrumentAudioContext.setSongVolume(newVolume);
    VocalsAudioContext.setSongVolume(newVolume);
  };

  const loadPatternFromCache = async (patternNumber) => {
    if (!patternNumber) {
      return null;
    }

    const cacheKey = String(patternNumber);

    if (patternCacheRef.current[cacheKey]) {
      return patternCacheRef.current[cacheKey];
    }

    try {
      const response = await databases.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_BEAT_PATTERN_ID,
        [
          Query.equal("patternNumber", parseInt(patternNumber, 10)),
          Query.equal("userEmail", user.email),
          Query.limit(1),
        ],
      );

      const document = response.documents[0];

      if (!document) {
        console.warn(`Pattern ${patternNumber} was not found.`);
        return null;
      }

      let parsedInstruments = {};
      let parsedKits = {};

      try {
        parsedInstruments = JSON.parse(document.instrumentsJson || "{}");
      } catch (error) {
        console.error(
          `Could not parse instruments for pattern ${patternNumber}:`,
          error,
        );
      }

      try {
        parsedKits = JSON.parse(document.kitsJson || "{}");
      } catch (error) {
        console.error(
          `Could not parse kits for pattern ${patternNumber}:`,
          error,
        );
      }

      /*
      Support instrumentsJson saved either as an object or array.
    */
      const normalizedInstruments = Array.isArray(parsedInstruments)
        ? parsedInstruments.reduce((result, instrument, index) => {
            result[`${instrument.InsName || "instrument"}-${index}`] =
              instrument;

            return result;
          }, {})
        : parsedInstruments;

      await Promise.all(
        Object.values(normalizedInstruments).map(async (instrument) => {
          if (!instrument?.InsName) return;

          try {
            const patternNotes = instrument.PianoRollPatternNo
              ? await fetchNotesPianoRoll(instrument.PianoRollPatternNo)
              : [];

            instrument.notes = Array.isArray(patternNotes) ? patternNotes : [];

            await InstrumentAudioContext.loadAudio(instrument.InsName);

            const instrumentVolume = Number(
              instrument.InsVolume ?? instrument.volume ?? 0.5,
            );

            InstrumentAudioContext.setInstVolume(
              instrumentVolume,
              instrument.InsName,
            );
          } catch (error) {
            console.error(
              `Could not load instrument ${instrument.InsName}:`,
              error,
            );

            instrument.notes = [];
          }
        }),
      );

      /*
      Preload every drum sample before playback starts.
    */
      await Promise.all(
        pads.map(async (pad) => {
          try {
            await InstrumentAudioContext.preloadAudio(pad.kitUrl);
          } catch (error) {
            console.error(
              `Could not preload drum sample ${pad.kitName}:`,
              error,
            );
          }
        }),
      );

      const kitStepLengths = Object.values(parsedKits || {}).map((kit) =>
        Array.isArray(kit?.sequence) ? kit.sequence.length : 16,
      );

      const instrumentStepLengths = Object.values(normalizedInstruments).map(
        (instrument) => {
          const noteSteps = (instrument.notes || [])
            .map((note) => {
              const [, step] = String(note).split(",");
              return Number(step) || 0;
            })
            .filter(Boolean);

          return noteSteps.length ? Math.max(...noteSteps) : 16;
        },
      );
      const rawStepCount = Math.max(
        16,
        ...kitStepLengths,
        ...instrumentStepLengths,
      );

      // Use the explicitly saved pattern size.
      // Old patterns where patternSize is null or missing default to 16.
      const savedPatternSize = Number(document.patternSize);

      const patternStepCount = savedPatternSize === 32 ? 32 : 16;

      if (!document.patternSize && rawStepCount > 16) {
        console.warn(
          `Pattern ${patternNumber} contains ${rawStepCount} detected steps but has no patternSize. Defaulting to 16.`,
        );
      }

      const loadedPattern = {
        instruments: normalizedInstruments,
        kits: parsedKits,
        stepCount: patternStepCount,
      };
      /*
      Update the ref immediately. Do not wait for React state.
    */
      patternCacheRef.current = {
        ...patternCacheRef.current,
        [cacheKey]: loadedPattern,
      };

      setPatternCache(patternCacheRef.current);

      return loadedPattern;
    } catch (error) {
      console.error(`Failed to load pattern ${patternNumber}:`, error);

      return null;
    }
  };
  const preloadSelectedSongPatterns = async () => {
    const patternNumbers = [
      ...new Set(selectedPatternsRef.current.filter(Boolean).map(String)),
    ];

    await Promise.all(
      patternNumbers.map((patternNumber) =>
        loadPatternFromCache(patternNumber),
      ),
    );

    const selectedVocalNumbers = [
      ...new Set(selectedVocalPatternsRef.current.filter(Boolean).map(String)),
    ];

    await Promise.all(
      selectedVocalNumbers.map(async (vocalNumber) => {
        const vocal = vocalPatternCache[String(vocalNumber)];

        if (!vocal?.audioUrl) return;

        try {
          await VocalsAudioContext.preload(String(vocalNumber), vocal.audioUrl);
        } catch (error) {
          console.error(`Could not preload vocal ${vocalNumber}:`, error);
        }
      }),
    );
  };
  const addPatternSelector = () => {
    setSelectedPatterns((prev) => [...prev, ""]);
  };
  const addVocalPatternSelector = () => {
    setSelectedVocalPatterns((prev) => [...prev, ""]);
  };
  const unloadPatternAudio = () => {
    setInstruments({});
    setKits({});
  };

  const handlePatternChange = async (index, event) => {
    const patternNumber = event.target.value;
    if (patternNumber === "edit") {
      const selectedPatternNumber = selectedPatterns[index];

      if (selectedPatternNumber && onEditPatternFromSong) {
        onEditPatternFromSong(selectedPatternNumber);
      }

      return;
    }
    if (patternNumber === "delete") {
      setSelectedPatterns((prev) => {
        const newPatterns = [...prev];
        newPatterns.splice(index, 1);
        return newPatterns;
      });
    } else {
      setSelectedPatterns((prev) => {
        const newPatterns = [...prev];
        newPatterns[index] = patternNumber;
        return newPatterns;
      });
      if (patternNumber) {
        await loadPatternFromCache(patternNumber);
      }
    }
  };

  const saveSongPattern = async () => {
    setIsSavingSong(true);
    const songPattern = JSON.stringify({
      patterns: selectedPatterns,
      vocalPatterns: selectedVocalPatterns,
      bpm,
    });
    try {
      const response = await databases.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_SONG_PATTERN_ID,
        [Query.equal("songName", songName), Query.equal("email", user.email)],
      );
      if (response.documents.length > 0) {
        // Update existing document
        const documentId = response.documents[0].$id;
        await databases.updateDocument(
          process.env.REACT_APP_DATABASE_ID,
          process.env.REACT_APP_COLLECTION_SONG_PATTERN_ID,
          documentId,
          {
            songPattern,
          },
        );
      } else {
        // Create new document
        await databases.createDocument(
          process.env.REACT_APP_DATABASE_ID,
          process.env.REACT_APP_COLLECTION_SONG_PATTERN_ID,
          ID.unique(),
          {
            songName,
            songPattern,
            email: user.email,
          },
        );
        fetchSongNames();
      }
    } catch (error) {
      console.error("Failed to save song pattern:", error);
    } finally {
      setIsSavingSong(false);
    }
  };

  const deleteSongPattern = async () => {
    setIsDeletingSong(true);
    try {
      const response = await databases.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_SONG_PATTERN_ID,
        [Query.equal("songName", songName), Query.equal("email", user.email)],
      );
      if (response.documents.length > 0) {
        const documentId = response.documents[0].$id;
        await databases.deleteDocument(
          process.env.REACT_APP_DATABASE_ID,
          process.env.REACT_APP_COLLECTION_SONG_PATTERN_ID,
          documentId,
        );
        setSongName("");
        setSelectedVocalPatterns(Array(5).fill(""));
        VocalsAudioContext.stopAll();
        fetchSongNames();
      }
    } catch (error) {
      console.error("Failed to delete song pattern:", error);
    } finally {
      setIsDeletingSong(false);
    }
  };

  const loadSongPattern = async (selectedSongName) => {
    try {
      const response = await databases.listDocuments(
        process.env.REACT_APP_DATABASE_ID,
        process.env.REACT_APP_COLLECTION_SONG_PATTERN_ID,
        [
          Query.equal("songName", selectedSongName),
          Query.equal("email", user.email),
        ],
      );
      const document = response.documents[0];
      if (document) {
        const {
          patterns = [],
          vocalPatterns = [],
          bpm: savedBpm,
        } = JSON.parse(document.songPattern);

        setSelectedPatterns(patterns);
        const cleanedVocalPatterns = vocalPatterns.map((value) => {
          if (
            value === undefined ||
            value === null ||
            value === "undefined" ||
            value === "null"
          ) {
            return "";
          }

          return String(value);
        });

        setSelectedVocalPatterns(
          cleanedVocalPatterns.length
            ? cleanedVocalPatterns
            : Array(patterns.length || 5).fill(""),
        );
        setBpm(savedBpm);
        setSongName(selectedSongName); // Set the song name in the text input
      }
    } catch (error) {
      console.error("Failed to load song pattern:", error);
    }
  };

  useEffect(() => {
    if (isPlaying) {
      startPlaying();
    } else {
      stopPlaying();
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isPlaying, bpm]);

  const handlePlayPause = async () => {
    if (isPlayingRef.current) {
      setIsPlaying(false);
      isPlayingRef.current = false;

      clearInterval(intervalRef.current);

      InstrumentAudioContext.stopAllNotes();
      VocalsAudioContext.stopAll();

      return;
    }

    if (playbackBusyRef.current) return;

    playbackBusyRef.current = true;

    try {
      await InstrumentAudioContext.resume?.();
      await VocalsAudioContext.resume();

      InstrumentAudioContext.setSongVolume(songVolume);
      VocalsAudioContext.setSongVolume(songVolume);

      /*
      This ensures every selected pattern has loaded samples,
      notes and instruments before the first playback step.
    */
      await preloadSelectedSongPatterns();

      const totalSlots = Math.max(
        selectedPatternsRef.current.length,
        selectedVocalPatternsRef.current.length,
        1,
      );

      if (currentPatternIndexRef.current >= totalSlots) {
        currentPatternIndexRef.current = 0;
        setCurrentPatternIndex(0);
      }

      await playVocalForSlot(currentPatternIndexRef.current);

      isPlayingRef.current = true;
      setIsPlaying(true);
    } catch (error) {
      console.error("Could not start song playback:", error);

      isPlayingRef.current = false;
      setIsPlaying(false);
    } finally {
      playbackBusyRef.current = false;
    }
  };

  const handleStop = () => {
    isPlayingRef.current = false;

    setIsPlaying(false);

    clearInterval(intervalRef.current);

    timelineStepRef.current = 0;
    activeBeatIndexRef.current = 0;
    activeVocalIndexRef.current = 0;

    setTimelineStep(0);
    setLedIndex(0);
    setCurrentPatternIndex(0);
    setActiveBeatIndex(0);
    setActiveVocalIndex(0);

    InstrumentAudioContext.stopAllNotes();
    VocalsAudioContext.stopAll();
  };
  const playVocalForSlot = async (slotIndex) => {
    VocalsAudioContext.stopAll();

    const selectedVocalId = selectedVocalPatternsRef.current[slotIndex];

    if (!selectedVocalId) return;

    const vocal = vocalPatternCache[String(selectedVocalId)];

    if (!vocal?.audioUrl) {
      console.warn(`No vocal audio found for ${selectedVocalId}`);

      return;
    }

    try {
      await VocalsAudioContext.play(
        String(selectedVocalId),
        vocal.audioUrl,
        Number(vocal.volume ?? 1),
        0,
      );
    } catch (error) {
      console.error(`Could not play vocal ${getVocalName(vocal)}:`, error);
    }
  };
  const normalizeKitName = (value = "") =>
    String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const getKitForPad = (patternKits, pad) => {
    if (!patternKits || !pad) return null;

    if (patternKits[pad.kitName]) {
      return patternKits[pad.kitName];
    }

    const normalizedPadName = normalizeKitName(pad.kitName);

    const matchingEntry = Object.entries(patternKits).find(([kitName]) => {
      return normalizeKitName(kitName) === normalizedPadName;
    });

    return matchingEntry?.[1] || null;
  };

  const playPatternStep = (patternData, oneBasedStep) => {
    if (!patternData) return;

    const patternInstruments = patternData.instruments || {};

    const patternKits = patternData.kits || {};

    Object.values(patternInstruments).forEach((instrument) => {
      if (!instrument?.InsName || !Array.isArray(instrument.notes)) {
        return;
      }

      const notesAtCurrentStep = instrument.notes.filter((savedNote) => {
        const [, savedStep] = String(savedNote).split(",");

        return parseInt(savedStep, 10) === oneBasedStep;
      });

      notesAtCurrentStep.forEach((savedNote) => {
        const [noteName] = String(savedNote).split(",");

        const noteIndex = notes.findIndex(
          (note) => note === String(noteName).trim(),
        );

        if (noteIndex === -1) return;

        const instrumentVolume = Number(
          instrument.InsVolume ?? instrument.volume ?? 0.5,
        );

        InstrumentAudioContext.setInstVolume(
          instrumentVolume,
          instrument.InsName,
        );

        InstrumentAudioContext.playPianoRollNote(
          noteIndex,
          notes,
          instrument.InsName,
        );
      });
    });

    pads.forEach((pad) => {
      const kit = getKitForPad(patternKits, pad);

      if (!kit?.sequence?.[oneBasedStep - 1]) {
        return;
      }

      const kitVolume = Number(kit.kitVolume ?? kit.volume ?? 0.5);

      InstrumentAudioContext.setKitVolume(kitVolume, pad.kitUrl);

      InstrumentAudioContext.playSound(pad.kitUrl, kitVolume);
    });
  };
  const getTrackTotalSteps = (selectedItems, getItemSteps) => {
    return selectedItems.reduce((total, item) => {
      if (!item) {
        return total + 16;
      }

      return total + getItemSteps(item);
    }, 0);
  };

  const getTrackPositionAtStep = (
    selectedItems,
    getItemSteps,
    absoluteStep,
  ) => {
    let accumulatedSteps = 0;

    for (let index = 0; index < selectedItems.length; index += 1) {
      const item = selectedItems[index];

      const itemSteps = item ? getItemSteps(item) : 16;

      const itemEndStep = accumulatedSteps + itemSteps;

      if (absoluteStep < itemEndStep) {
        return {
          index,
          item,
          localStep: absoluteStep - accumulatedSteps,
          startStep: accumulatedSteps,
          endStep: itemEndStep,
          stepCount: itemSteps,
        };
      }

      accumulatedSteps = itemEndStep;
    }

    return {
      index: -1,
      item: "",
      localStep: 0,
      startStep: accumulatedSteps,
      endStep: accumulatedSteps,
      stepCount: 16,
    };
  };

  const getSongTotalSteps = () => {
    const beatSteps = getTrackTotalSteps(
      selectedPatternsRef.current,
      getPatternStepCount,
    );

    const vocalSteps = getTrackTotalSteps(
      selectedVocalPatternsRef.current,
      getVocalStepCount,
    );

    return Math.max(beatSteps, vocalSteps, 16);
  };
  const playVocalById = async (vocalId) => {
    if (!vocalId) return;

    const vocal = vocalPatternCache[String(vocalId)];

    if (!vocal?.audioUrl) {
      console.warn(`No vocal audio found for ${vocalId}`);
      return;
    }

    try {
      VocalsAudioContext.stopAll();

      await VocalsAudioContext.play(
        String(vocalId),
        vocal.audioUrl,
        Number(vocal.volume ?? 1),
        0,
      );
    } catch (error) {
      console.error(`Could not play vocal ${getVocalName(vocal)}:`, error);
    }
  };
  const startPlaying = () => {
    clearInterval(intervalRef.current);

    const stepIntervalMs = ((60 / Math.max(1, Number(bpm) || 120)) * 1000) / 4;

    intervalRef.current = setInterval(() => {
      if (!isPlayingRef.current) return;

      const absoluteStep = timelineStepRef.current;

      const beatPosition = getTrackPositionAtStep(
        selectedPatternsRef.current,
        getPatternStepCount,
        absoluteStep,
      );

      const vocalPosition = getTrackPositionAtStep(
        selectedVocalPatternsRef.current,
        getVocalStepCount,
        absoluteStep,
      );

      /*
      Update beat active clip independently.
    */
      if (activeBeatIndexRef.current !== beatPosition.index) {
        activeBeatIndexRef.current = beatPosition.index;

        setActiveBeatIndex(beatPosition.index);
      }

      /*
      Update vocal active clip independently.
    */
      if (activeVocalIndexRef.current !== vocalPosition.index) {
        activeVocalIndexRef.current = vocalPosition.index;

        setActiveVocalIndex(vocalPosition.index);
      }

      /*
      Play the beat/instrument step belonging to the
      current absolute song position.
    */
      if (beatPosition.item) {
        const patternData = patternCacheRef.current[String(beatPosition.item)];

        playPatternStep(patternData, beatPosition.localStep + 1);
      }

      /*
      Start a vocal exactly at its own timeline boundary.

      Example:
      Beat 1 = 32 steps
      Vocal 1 = 16 steps
      Vocal 2 = 16 steps

      Vocal 1 starts at absolute step 0.
      Vocal 2 starts at absolute step 16 while Beat 1 continues.
    */
      if (vocalPosition.item && vocalPosition.localStep === 0) {
        playVocalById(vocalPosition.item);
      }

      const songTotalSteps = getSongTotalSteps();

      const nextAbsoluteStep = (absoluteStep + 1) % songTotalSteps;

      /*
      Stop held notes before moving into another beat clip.
    */
      const nextBeatPosition = getTrackPositionAtStep(
        selectedPatternsRef.current,
        getPatternStepCount,
        nextAbsoluteStep,
      );

      if (nextBeatPosition.index !== beatPosition.index) {
        InstrumentAudioContext.stopAllNotes();
      }

      /*
      When the entire song loops, restart vocals cleanly.
    */
      if (nextAbsoluteStep === 0) {
        InstrumentAudioContext.stopAllNotes();
        VocalsAudioContext.stopAll();
      }

      timelineStepRef.current = nextAbsoluteStep;

      setTimelineStep(nextAbsoluteStep);

      /*
      Keep these only if some other part of the UI
      still needs them.
    */
      setLedIndex(beatPosition.localStep + 1);

      setCurrentPatternIndex(beatPosition.index);
    }, stepIntervalMs);
  };

  const stopPlaying = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;

    InstrumentAudioContext.stopAllNotes();
    VocalsAudioContext.stopAll();
  };

  const getTimelineClipWidth = (stepLength = 16) => {
    const baseWidth = Number(dropdownWidth);
    const safeSteps = Math.max(16, Number(stepLength) || 16);
    const units = safeSteps / 16;

    /*
    This includes the internal spaces that would normally occur
    between multiple 16-step clips.

    16 steps = baseWidth
    32 steps = baseWidth * 2 + one gap
    64 steps = baseWidth * 4 + three gaps
  */
    return baseWidth * units + TIMELINE_GAP * (units - 1);
  };

  const getPatternStepCount = (patternNumber) => {
    if (!patternNumber) return 16;

    const cachedPattern =
      patternCacheRef?.current?.[String(patternNumber)] ||
      patternCache[String(patternNumber)];

    return Number(cachedPattern?.stepCount) || 16;
  };

  const getVocalStepCount = (vocalPatternNumber) => {
    if (!vocalPatternNumber) return 16;

    const vocal = vocalPatternCache[String(vocalPatternNumber)];

    return Number(vocal?.stepCount) || 16;
  };

  const getSelectorWidth = (patternNumber) => {
    return getTimelineClipWidth(getPatternStepCount(patternNumber));
  };

  const getVocalSelectorWidth = (vocalPatternNumber) => {
    return getTimelineClipWidth(getVocalStepCount(vocalPatternNumber));
  };
  return (
    <div className={`song-player ${isPlaying ? "playing" : ""}`}>
      <div className="song-player-controls">
        <div className="song-head1">
          {" "}
          <h3>SONG</h3>
          <button onClick={handlePlayPause} className="play-button-insplayer">
            {isPlaying ? <FaPause /> : <FaPlay />}
          </button>
          <button onClick={handleStop} className="stop-button2">
            <FaStop />
          </button>
          <div>
            <label htmlFor="song-volume"></label>
            <input
              type="range"
              id="song-volume"
              min="0"
              max="1"
              step="0.01"
              className="vocal-volume"
              value={songVolume}
              onChange={handleSongVolumeChange}
            />
          </div>{" "}
        </div>
        <div className="song-head1">
          {" "}
          <select
            onChange={(e) => loadSongPattern(e.target.value)}
            value={songName}
            className="song-pattern-dropdown "
          >
            <option value="" disabled className="song-pattern-option ">
              Select Song
            </option>
            {songNames.map((name, index) => (
              <option key={index} value={name}>
                {name}
              </option>
            ))}
          </select>{" "}
          <input
            type="text"
            placeholder="Enter song name"
            value={songName}
            onChange={(e) => setSongName(e.target.value)}
          />{" "}
          <button
            onClick={saveSongPattern}
            className="save-pattern-button"
            disabled={isSavingSong || !songName}
          >
            {isSavingSong ? <ClipLoader size={20} color="#fff" /> : <FaSave />}
          </button>
          <button
            onClick={deleteSongPattern}
            className="delete-pattern-button"
            disabled={!songName}
          >
            {isDeletingSong ? (
              <ClipLoader size={20} color="#fff" />
            ) : (
              <FaTrash />
            )}
          </button>
          <button
            type="button"
            className="export-song-button"
            disabled={
              isExportingSong ||
              (!selectedPatterns.some(Boolean) &&
                !selectedVocalPatterns.some(Boolean))
            }
            onClick={async () => {
              /*
      Stop current real-time playback first so
      rendering does not play over the song.
    */
              handleStop();

              /*
      Ensure selected beat patterns contain their
      notes and samples before export starts.
    */
              await preloadSelectedSongPatterns();

              setExportRequest((previous) => previous + 1);
            }}
            title="Render the complete song as WAV"
          >
            {isExportingSong ? (
              <ClipLoader size={18} color="#fff" />
            ) : (
              <>
                <FaFileExport />
                <span>Export WAV</span>
              </>
            )}
          </button>
        </div>
      </div>{" "}
      <div className="p-sel-container">
        <div className="song-timeline">
          <div className="song-row">
            <div className="song-row-label">B</div>

            <div className="pattern-selectors">
              {selectedPatterns.map((pattern, index) => (
                <select
                  key={index}
                  value={pattern}
                  onChange={(event) => handlePatternChange(index, event)}
                  className={`pattern-dropdown1 ${
                    isPlaying && activeBeatIndex === index
                      ? "active-pattern"
                      : ""
                  }`}
                  style={{ width: `${getSelectorWidth(pattern)}px` }}
                >
                  <option value="">{index + 1}</option>

                  {beatPatterns.map((pattern, patternIndex) => (
                    <option
                      key={`beat-${pattern.$id || pattern.patternNumber}-${patternIndex}`}
                      value={pattern.patternNumber}
                    >
                      p{pattern.patternNumber}
                    </option>
                  ))}

                  {pattern && <option value="edit">EDIT</option>}
                  <option value="delete">Delete</option>
                </select>
              ))}{" "}
              <button className="add-pattern-slot" onClick={addPatternSelector}>
                <FaPlus />
              </button>
            </div>
          </div>

          <div className="song-row">
            <div className="song-row-label vocal-label">V</div>

            <div className="pattern-selectors">
              {selectedVocalPatterns.map((vocal, index) => (
                <select
                  key={index}
                  value={vocal}
                  onChange={(event) => {
                    const value = event.target.value;

                    if (value === "delete") {
                      setSelectedVocalPatterns((previousPatterns) => {
                        const nextPatterns = [...previousPatterns];
                        nextPatterns.splice(index, 1);
                        return nextPatterns;
                      });

                      if (currentPatternIndex === index) {
                        VocalsAudioContext.stopAll();
                      }

                      return;
                    }

                    setSelectedVocalPatterns((previousPatterns) => {
                      const nextPatterns = [...previousPatterns];
                      nextPatterns[index] = value;
                      return nextPatterns;
                    });

                    const selectedVocal = vocalPatternCache[String(value)];

                    if (selectedVocal?.audioUrl) {
                      VocalsAudioContext.preload(
                        String(value),
                        selectedVocal.audioUrl,
                      ).catch((error) => {
                        console.error(
                          "Could not preload selected vocal:",
                          error,
                        );
                      });
                    }
                  }}
                  className={`pattern-dropdown1 vocal-dropdown ${
                    isPlaying && activeVocalIndex === index
                      ? "active-pattern"
                      : ""
                  }`}
                  style={{
                    width: `${getVocalSelectorWidth(vocal)}px`,
                  }}
                >
                  <option value="">{index + 1}</option>

                  {vocalPatterns.map((vocalPattern, vocalIndex) => {
                    const vocalId = getVocalId(vocalPattern);
                    const vocalName = getVocalName(vocalPattern);

                    if (!vocalId) return null;

                    return (
                      <option
                        key={`vocal-${vocalPattern.$id || vocalId}-${vocalIndex}`}
                        value={vocalId}
                      >
                        {vocalName}
                      </option>
                    );
                  })}

                  <option value="delete">Delete</option>
                </select>
              ))}
              <button
                className="add-pattern-slot"
                onClick={addVocalPatternSelector}
              >
                <FaPlus />
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="led-n-slider">
        {" "}
        {/* <div className="led-container">{renderLEDIndicators()}</div> */}
        <div className="slider-container">
          <input
            type="range"
            min="20"
            max="92"
            value={dropdownWidth}
            onChange={(e) => setDropdownWidth(e.target.value)}
            className="dropdown-width-slider"
          />
        </div>
      </div>
      <ExportSong
        exportRequest={exportRequest}
        bpm={bpm}
        songName={songName}
        songVolume={songVolume}
        pads={pads}
        selectedPatterns={selectedPatterns}
        selectedVocalPatterns={selectedVocalPatterns}
        patternCache={patternCacheRef.current}
        vocalPatternCache={vocalPatternCache}
        loadPatternFromCache={loadPatternFromCache}
        onExportingChange={setIsExportingSong}
      />
    </div>
  );
};

export default SongPlayer;
